import { useCallback, useMemo } from "react";
import { Dimensions, NativeScrollEvent, NativeSyntheticEvent } from "react-native";
import { useAnimatedStyle, withTiming } from "react-native-reanimated";
import { useScreenSize } from "@lookiero/sty-psp-ui";
import { useVisibilityForVerticalScroll } from "../../../hooks/useVisibilityForVerticalScroll";

const LARGE_HEADER_HEIGHT = 48;
const SMALL_HEADER_HEIGHT = 55;
const TRANSITION_DELAY = 250;
const { height } = Dimensions.get("window");

interface UseHideHeaderOnScrollFunctionArgs {
  readonly onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
}

const useHideHeaderOnScroll = ({ onScroll }: UseHideHeaderOnScrollFunctionArgs = {}) => {
  const { visible: headerIsVisible, handleOnScroll: visibilityOnScroll } = useVisibilityForVerticalScroll();
  const screen = useScreenSize();

  const headerHeight = useMemo(() => (screen === "L" ? LARGE_HEADER_HEIGHT : SMALL_HEADER_HEIGHT), [screen]);

  const headerAnimatedStyle = useAnimatedStyle(
    () => ({
      transform: [{ translateY: withTiming(headerIsVisible ? 0 : -headerHeight, { duration: TRANSITION_DELAY }) }],
    }),
    [headerHeight, headerIsVisible],
  );

  const scrollViewAnimatedStyle = useAnimatedStyle(
    () => ({
      height: withTiming(headerIsVisible ? height - headerHeight : height, {
        duration: TRANSITION_DELAY,
      }),
    }),
    [headerHeight, headerIsVisible],
  );

  const handleOnScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      visibilityOnScroll(event);
      onScroll?.(event);
    },
    [onScroll, visibilityOnScroll],
  );

  return { headerAnimatedStyle, scrollViewAnimatedStyle, handleOnScroll };
};

export { useHideHeaderOnScroll };
